import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import DashboardCard07 from '../../partials/dashboard/DashboardCard07';
import DashboardCard07Prod from '../../partials/dashboard/DashboardCard07Prod';
import DashboardCard07physicians from "../../partials/dashboard/DashboardCard07physicians";
import { AccountDetailsForm } from '../../components/account/account-details-form';
import { AccountInfo } from '../../components/account/account-info';
import { jwtDecode } from 'jwt-decode';
import {
  HiOutlineUsers,
  HiOutlineBuildingOffice2,
  HiOutlineHeart,
  HiOutlineFingerPrint,
  HiChevronRight
} from "react-icons/hi2";

type JwtPayload = {
  userId: number;
  username: string;
  role: string;
};

export default function Page({ className = "" }: { className?: string }): React.JSX.Element {
  const location = useLocation();
  const { item: targetUser } = location.state || {};
  const [user, setUser] = useState<JwtPayload | null>(null);
  const [fetchUsersCallback, setFetchUsersCallback] = useState<() => void>(() => () => { });
  const [activeTab, setActiveTab] = useState("dashboard1");

  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
      try {
        const decoded: JwtPayload = jwtDecode(token);
        setUser(decoded);
      } catch (error) {
        console.error('Error decoding the token:', error);
      }
    }
  }, []);

  const userDisplayName = targetUser?.user_name_surname || targetUser?.login || user?.username || "Anatolie Bondarenco";

  const dashboardTabs = [
    {
      id: "dashboard1",
      label: "Acces utilizatori",
      icon: <HiOutlineUsers className="w-4 h-4" />,
      component: (
        <div className="w-full">
          {user?.role === "User" ? (
            <div className="bg-white dark:bg-gray-800 border border-[#E7EAF0] dark:border-gray-700/60 rounded-2xl p-8 text-center text-gray-500">
              Fără date de acces pentru utilizatori.
            </div>
          ) : (
            <DashboardCard07 />
          )}
        </div>
      )
    },
    {
      id: "dashboard2",
      label: "Acces producători",
      icon: <HiOutlineBuildingOffice2 className="w-4 h-4" />,
      component: (
        <div className="w-full">
          <DashboardCard07Prod />
        </div>
      )
    },
    {
      id: "physicians",
      label: "Doctori favoriți",
      icon: <HiOutlineHeart className="w-4 h-4" />,
      component: (
        <div className="w-full">
          <DashboardCard07physicians />
        </div>
      )
    },
  ];

  const isAdmin = user?.role === "Admin";

  return (
    <div className={`w-full ${className}`}>
      {/* Top Section: Profile Card & Profile Details Form */}
      <div className={`grid grid-cols-1 ${isAdmin ? 'lg:grid-cols-12' : ''} gap-5 sm:gap-6 items-start`}>
        {/* Left: Profile Summary Card (4 cols) */}
        <div className={isAdmin ? "lg:col-span-4 w-full" : "max-w-md mx-auto w-full"}>
          <AccountInfo setFetchUsersCallback={setFetchUsersCallback} />
        </div>

        {/* Right: Profile Details Form (Admin only, 8 cols) */}
        {isAdmin && (
          <div className="lg:col-span-8 w-full">
            <AccountDetailsForm onSaveChanges={fetchUsersCallback} />
          </div>
        )}
      </div>

      {/* Tabs Navigation Shell */}
      <div className="mt-6 mb-4">
        <div className="bg-white dark:bg-gray-800 border border-[#E5E8EF] dark:border-gray-700/60 rounded-xl p-1 shadow-xs grid grid-cols-1 sm:grid-cols-3 gap-1">
          {dashboardTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`h-[38px] rounded-[9px] flex items-center justify-center gap-2 text-[12px] transition-all duration-150 cursor-pointer ${
                  isActive
                    ? "bg-[#F0EDFF] text-[#6755E8] font-semibold shadow-[0_1px_3px_rgba(60,50,130,0.06)] dark:bg-indigo-950/70 dark:text-indigo-300"
                    : "text-[#697386] hover:text-[#172033] hover:bg-gray-50/80 dark:text-gray-400 dark:hover:bg-gray-700/50 font-medium"
                }`}
              >
                <span className={isActive ? "text-[#6755E8] dark:text-indigo-300" : "text-[#8E97A6]"}>
                  {tab.icon}
                </span>
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span
                    className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                      isActive
                        ? "bg-[#6755E8] text-white"
                        : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Panel Content with Smooth Animation */}
      <div key={activeTab} className="w-full animate-tabFadeIn pb-10">
        {dashboardTabs.find((t) => t.id === activeTab)?.component}
      </div>
    </div>
  );
}
